import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
    Container, Typography, Box, Paper, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, TextField,
    Menu, MenuItem, TablePagination, Fab, InputAdornment, Chip, Stack,
    ToggleButton, ToggleButtonGroup, CircularProgress, Autocomplete,
    useMediaQuery, useTheme, alpha
} from '@mui/material';
import FormDialog, { FormField, FormSection, arborGreen, creamBg, filledInputSx } from '../components/FormDialog';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RepeatIcon from '@mui/icons-material/Repeat';
import Tooltip from '@mui/material/Tooltip';
import ReceiptCameraDialog from '../components/ReceiptCameraDialog';
import LoadingOverlay from '../components/LoadingOverlay';
import ScanDocumentDialog from '../components/ScanDocumentDialog';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import QuickCategorySelectDialog from '../components/QuickCategorySelectDialog';
import { getTodayInTimezone, formatDateInTimezone } from '../utils/date';
import { useTranslation } from 'react-i18next';
import { Currency } from '../constants/enums';
import { sortCategoriesHierarchically, filterAndSortCategories, getCategoryOptionLabel, findMatchingCategory } from '../utils/category';
import { handleNumericFocus } from '../utils/inputUtils';


// Memoized row for desktop view
const TransactionRow = React.memo(({ row, userRole, handleMenuClick, formatCurrency, onOpenQuickCategory }) => {
    const { t, i18n } = useTranslation();
    const canEditCategory = userRole !== 'viewer' && !row.isTransfer;

    return (
        <TableRow hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {new Date(row.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                    {row.recurring_transaction_id && (
                        <Tooltip title={t("Automatically generated by a recurring transaction")}>
                            <RepeatIcon fontSize="small" color="action" sx={{ fontSize: '1rem', opacity: 0.7 }} />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>{row.description}</TableCell>
            <TableCell>
                {row.isTransfer ? (
                    <Chip
                        size="small"
                        label={`${row.fromAcc ? `${t(row.fromAcc.account_type)} (${row.fromAcc.currency})` : t('Unknown Account')} → ${row.toAcc ? `${t(row.toAcc.account_type)} (${row.toAcc.currency})` : t('Unknown Account')}`}
                        sx={{
                            bgcolor: 'rgba(1, 45, 29, 0.08)',
                            color: arborGreen,
                            borderColor: 'rgba(1, 45, 29, 0.2)',
                            border: 1
                        }}
                    />
                ) : canEditCategory ? (
                    <Tooltip title={t('Click to change category')} arrow placement="top">
                        <Chip
                            size="small"
                            icon={<EditIcon sx={{ fontSize: '0.8rem !important', opacity: 0.75 }} />}
                            label={row.category ? t(row.category.name) : t('Uncategorized')}
                            onClick={() => onOpenQuickCategory(row)}
                            variant={row.category ? 'outlined' : 'filled'}
                            sx={{
                                cursor: 'pointer',
                                bgcolor: row.category?.color ? `${row.category.color}15` : (row.category ? 'transparent' : alpha('#000', 0.06)),
                                color: row.category?.color || (row.category ? 'primary.main' : 'text.secondary'),
                                borderColor: row.category?.color || (row.category ? 'primary.main' : 'divider'),
                                border: 1,
                                fontSize: '0.75rem',
                                height: 24,
                                fontStyle: row.category ? 'normal' : 'italic',
                                fontWeight: 600,
                                '&:hover': {
                                    bgcolor: row.category?.color ? `${row.category.color}25` : alpha('#000', 0.12),
                                    borderColor: row.category?.color || 'primary.main',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                },
                                transition: 'all 0.15s ease'
                            }}
                        />
                    </Tooltip>
                ) : row.category ? (
                    <Chip
                        size="small"
                        label={t(row.category.name)}
                        sx={{
                            bgcolor: row.category.color ? `${row.category.color}20` : 'transparent',
                            color: row.category.color || 'primary.main',
                            borderColor: row.category.color || 'primary.main',
                            border: 1
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {t('Uncategorized')}
                    </Typography>
                )}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
                {row.isTransfer ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
                            {formatCurrency(row.from_amount, row.currency)}
                        </Typography>
                        <Typography component="span" sx={{ opacity: 0.6, fontSize: '0.75rem', fontWeight: 600, mr: 0.5 }}>
                            {row.currency}
                        </Typography>
                        <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem', mx: 0.25 }}>→</Typography>
                        <Typography component="span" sx={{ fontSize: '0.875rem', color: 'success.main', fontWeight: 600 }}>
                            {formatCurrency(row.to_amount, row.toAcc?.currency || row.currency)}
                        </Typography>
                        <Typography component="span" sx={{ opacity: 0.6, fontSize: '0.75rem', fontWeight: 600 }}>
                            {row.toAcc?.currency || row.currency}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography component="span" sx={{
                                fontWeight: 600,
                                color: Number(row.amount) >= 0 ? 'text.primary' : 'success.main'
                            }}>
                                {formatCurrency(row.amount, row.currency)}
                            </Typography>
                            <Typography component="span" variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
                                {row.currency}
                            </Typography>
                        </Box>
                        {row.exchanged_amount && (row.currency || 'USD') !== row.exchanged_currency && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    opacity: 0.7,
                                    mt: 0.5
                                }}
                            >
                                ≈ {formatCurrency(row.exchanged_amount, row.exchanged_currency)} {row.exchanged_currency}
                            </Typography>
                        )}
                    </Box>
                )}
            </TableCell>
            {userRole !== 'viewer' && (
                <TableCell align="right">
                    <IconButton size="small" onClick={(e) => handleMenuClick(e, row)} sx={{ color: 'text.secondary' }}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </TableCell>
            )}
        </TableRow>
    );
});

// Memoized card for mobile view
const TransactionCard = React.memo(({ row, userRole, handleMenuClick, formatCurrency, onOpenQuickCategory }) => {
    const { t, i18n } = useTranslation();
    const canEditCategory = userRole !== 'viewer' && !row.isTransfer;

    return (
        <Paper elevation={0} sx={{ p: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {new Date(row.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                </Typography>
                <IconButton size="small" onClick={(e) => handleMenuClick(e, row)} sx={{ color: 'text.secondary', mt: -0.5, mr: -0.5 }}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Box>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>{row.description}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.isTransfer ? (
                        <Chip
                            size="small"
                            label={`${row.fromAcc ? `${t(row.fromAcc.account_type)} (${row.fromAcc.currency})` : t('Unknown Account')} → ${row.toAcc ? `${t(row.toAcc.account_type)} (${row.toAcc.currency})` : t('Unknown Account')}`}
                            sx={{
                                bgcolor: 'rgba(1, 45, 29, 0.08)',
                                color: arborGreen,
                                borderColor: 'rgba(1, 45, 29, 0.2)',
                                border: 1,
                                fontSize: '0.75rem',
                                height: 24
                            }}
                        />
                    ) : canEditCategory ? (
                        <Tooltip title={t('Click to change category')} arrow placement="top">
                            <Chip
                                size="small"
                                icon={<EditIcon sx={{ fontSize: '0.8rem !important', opacity: 0.75 }} />}
                                label={row.category ? t(row.category.name) : t('Uncategorized')}
                                onClick={() => onOpenQuickCategory(row)}
                                variant={row.category ? 'outlined' : 'filled'}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: row.category?.color ? `${row.category.color}15` : (row.category ? 'transparent' : alpha('#000', 0.06)),
                                    color: row.category?.color || (row.category ? 'primary.main' : 'text.secondary'),
                                    borderColor: row.category?.color || (row.category ? 'primary.main' : 'divider'),
                                    border: 1,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    fontStyle: row.category ? 'normal' : 'italic',
                                    fontWeight: 600,
                                    '&:hover': {
                                        bgcolor: row.category?.color ? `${row.category.color}25` : alpha('#000', 0.12),
                                        borderColor: row.category?.color || 'primary.main',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.15s ease'
                                }}
                            />
                        </Tooltip>
                    ) : row.category ? (
                        <Chip
                            size="small"
                            label={t(row.category.name)}
                            sx={{
                                bgcolor: row.category.color ? `${row.category.color}15` : 'transparent',
                                color: row.category.color || 'primary.main',
                                borderColor: row.category.color || 'primary.main',
                                border: 1,
                                fontSize: '0.75rem',
                                height: 24
                            }}
                        />
                    ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('Uncategorized')}
                        </Typography>
                    )}
                    {row.recurring_transaction_id && (
                        <RepeatIcon sx={{ fontSize: '1rem', opacity: 0.5 }} />
                    )}
                </Box>
                {row.isTransfer ? (
                    <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                        {formatCurrency(row.from_amount, row.currency)} {row.currency} → {formatCurrency(row.to_amount, row.toAcc?.currency || row.currency)} {row.toAcc?.currency || row.currency}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography sx={{
                                fontWeight: 700,
                                color: Number(row.amount) >= 0 ? 'text.primary' : 'success.main'
                            }}>
                                {formatCurrency(row.amount, row.currency)}
                            </Typography>
                            <Typography component="span" variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
                                {row.currency}
                            </Typography>
                        </Box>
                        {row.exchanged_amount && (row.currency || 'USD') !== row.exchanged_currency && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    opacity: 0.7,
                                    mt: 0.25
                                }}
                            >
                                ≈ {formatCurrency(row.exchanged_amount, row.exchanged_currency)} {row.exchanged_currency}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Paper>
    );
});

export default function LedgerTransactions() {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const { api, preferredCurrency, preferredTimezone, formatCurrency } = useAuth();
    const { showToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Data State
    const [entries, setEntries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [userRole, setUserRole] = useState('viewer');
    const [error, setError] = useState('');
    const [receiptError, setReceiptError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Utility function for setting default dates
    const getDefaultDates = useCallback(() => {
        const todayStr = getTodayInTimezone(preferredTimezone);
        const [y, m] = todayStr.split('-').map(Number);
        const startYear = y - 1;
        const startMonth = String(m).padStart(2, '0');
        return {
            start: `${startYear}-${startMonth}-01`,
            end: todayStr
        };
    }, [preferredTimezone]);

    const initialDates = getDefaultDates();

    // Filter & Pagination State
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [activeFilter, setActiveFilter] = useState('1 Year'); // To highlight active chip
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Accounts and Transfers State
    const [accounts, setAccounts] = useState([]);
    const accountsRef = React.useRef(accounts);
    accountsRef.current = accounts;
    const [showTransfers, setShowTransfers] = useState(false);

    // Dialog State
    const [open, setOpen] = useState(false);
    const [currentEntry, setCurrentEntry] = useState(null); // null = create mode
    const [selectedTxForCategory, setSelectedTxForCategory] = useState(null);
    const [isQuickCategorySaving, setIsQuickCategorySaving] = useState(false);
    const [isParsingReceipt, setIsParsingReceipt] = useState(false);
    const [formData, setFormData] = useState({
        description: '',
        amount: '',
        currency: preferredCurrency,
        date: '',
        category_id: '',
        type: 'Debit',
        payment_method: 'CASH',
        credit_card_id: '',
        cash_back_amount: '',
        source_document_id: null,
        from_account_id: '',
        to_account_id: '',
        exchange_rate: '',
        to_amount: '',
        feedback: ''
    });

    const [creditCards, setCreditCards] = useState([]);
    const [isSyncingCashback, setIsSyncingCashback] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleSelectQuickCategory = async (category) => {
        if (!selectedTxForCategory) return;
        const entry = selectedTxForCategory;
        setIsQuickCategorySaving(true);
        try {
            const newCatId = category?.id ? Number(category.id) : null;
            const payload = {
                description: entry.description,
                amount: entry.amount,
                currency: entry.currency || 'USD',
                date: entry.date,
                category_id: newCatId,
                recurring_transaction_id: entry.recurring_transaction_id || null,
                credit_card_id: entry.credit_card_id || null,
                source_document_id: entry.source_document_id || null,
                cash_back_amount: entry.cash_back_amount || null,
            };

            await api.put(`/ledger/${entry.id}`, payload);
            setSelectedTxForCategory(null);
            showToast(t('Category updated successfully'), 'success');
            fetchEntries();
        } catch (err) {
            console.error("Failed to update category", err);
            showToast(err.response?.data?.detail || t('Failed to update category'), 'error');
        } finally {
            setIsQuickCategorySaving(false);
        }
    };

    // Camera Scanner State
    const [cameraOpen, setCameraOpen] = useState(false);

    // Scan Document Dialog State
    const [scanDocOpen, setScanDocOpen] = useState(false);

    // Menu State
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedEntryId, setSelectedEntryId] = useState(null);

    const fetchEntries = useCallback(async () => {
        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', new Date(startDate).toISOString());
            if (endDate) params.append('end_date', new Date(endDate).toISOString());
            if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
            if (selectedCategories.length > 0) {
                selectedCategories.forEach(cat => params.append('category_ids', cat.id));
            }
            params.append('include_exchanged_amount', 'true');

            if (!showTransfers) {
                params.append('skip', page * rowsPerPage);
                params.append('limit', rowsPerPage);
                const response = await api.get(`/ledger/ledgers/${id}/entries?${params.toString()}`);
                const entriesData = response.data?.items || (Array.isArray(response.data) ? response.data : []);
                setEntries(entriesData);
                setTotalCount(response.data?.total !== undefined ? response.data.total : entriesData.length);
            } else {
                // Fetch bounded range of entries and transfers to avoid pulling thousands into client memory
                const fetchLimit = Math.min((page + 1) * rowsPerPage + rowsPerPage, 500);
                params.append('skip', 0);
                params.append('limit', fetchLimit);

                const tParams = new URLSearchParams();
                if (startDate) tParams.append('start_date', startDate);
                if (endDate) tParams.append('end_date', endDate);
                tParams.append('skip', 0);
                tParams.append('limit', fetchLimit);

                const [entriesRes, transfersRes] = await Promise.all([
                    api.get(`/ledger/ledgers/${id}/entries?${params.toString()}`),
                    api.get(`/ledger/ledgers/${id}/account-transfers?${tParams.toString()}`)
                ]);

                const entriesData = entriesRes.data?.items || (Array.isArray(entriesRes.data) ? entriesRes.data : []);
                const transfersData = transfersRes.data || [];

                const currentAccounts = accountsRef.current || [];
                const formattedTransfers = transfersData.map(t => {
                    const fromAcc = currentAccounts.find(a => String(a.id) === String(t.from_account_id));
                    const toAcc = currentAccounts.find(a => String(a.id) === String(t.to_account_id));
                    const fromLabel = fromAcc ? `${fromAcc.account_type} (${fromAcc.currency})` : 'Unknown Account';
                    const toLabel = toAcc ? `${toAcc.account_type} (${toAcc.currency})` : 'Unknown Account';

                    return {
                        id: `transfer-${t.id}`,
                        transferId: t.id,
                        date: t.date,
                        description: t.description || 'Account Transfer',
                        isTransfer: true,
                        amount: -Number(t.from_amount),
                        currency: fromAcc?.currency || 'CAD',
                        from_account_id: t.from_account_id,
                        to_account_id: t.to_account_id,
                        from_amount: t.from_amount,
                        to_amount: t.to_amount,
                        exchange_rate: t.exchange_rate,
                        transferLabel: `${fromLabel} → ${toLabel}`,
                        fromAcc,
                        toAcc
                    };
                });

                let filteredTransfers = formattedTransfers;
                if (debouncedSearch.trim()) {
                    const searchLower = debouncedSearch.trim().toLowerCase();
                    filteredTransfers = formattedTransfers.filter(t =>
                        t.description.toLowerCase().includes(searchLower) ||
                        t.transferLabel.toLowerCase().includes(searchLower)
                    );
                }

                const merged = [...entriesData, ...filteredTransfers];
                merged.sort((a, b) => new Date(b.date) - new Date(a.date));

                setTotalCount(entriesRes.data?.total !== undefined ? entriesRes.data.total + filteredTransfers.length : merged.length);
                setEntries(merged.slice(page * rowsPerPage, (page + 1) * rowsPerPage));
            }
        } catch (err) {
            console.error("Failed to fetch entries", err);
            setError('Failed to load transactions.');
        } finally {
            setIsLoading(false);
        }
    }, [api, id, page, rowsPerPage, startDate, endDate, debouncedSearch, selectedCategories, showTransfers]);

    const fetchLedgerDetails = useCallback(async () => {
        try {
            const response = await api.get('/ledger/ledgers');
            const currentLedger = response.data.find(l => l.id?.toString() === id?.toString());
            if (currentLedger) {
                setUserRole(currentLedger.role || 'viewer');
            }
        } catch (err) {
            console.error("Failed to fetch ledger details", err);
        }
    }, [api, id]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await api.get(`/categories/ledger/${id}`);
            setCategories(response.data);
        } catch (err) {
            console.error("Failed to fetch categories", err);
        }
    }, [api, id]);

    const fetchCreditCards = useCallback(async () => {
        try {
            const response = await api.get(`/credit-cards/ledger/${id}`);
            setCreditCards(response.data);
        } catch (err) {
            console.error("Failed to fetch credit cards", err);
        }
    }, [api, id]);

    const fetchAccounts = useCallback(async () => {
        try {
            const response = await api.get(`/ledger/ledgers/${id}/accounts`);
            setAccounts(response.data);
        } catch (err) {
            console.error("Failed to fetch accounts", err);
        }
    }, [api, id]);

    const fetchAndSetExchangeRate = useCallback(async (fromCurrency, toCurrency, txDate) => {
        if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) {
            setFormData(prev => {
                const updated = { ...prev, exchange_rate: '1.0' };
                const cleanAmount = parseFloat((prev.amount || '0').replace(/,/g, '')) || 0;
                updated.to_amount = String(cleanAmount);
                return updated;
            });
            return;
        }
        try {
            const todayStr = getTodayInTimezone(preferredTimezone);
            const dateParam = (txDate && txDate > todayStr) ? todayStr : (txDate || todayStr);
            const params = new URLSearchParams({
                from_currencies: fromCurrency,
                to_currency: toCurrency,
                date: dateParam
            });
            const res = await api.get(`/ledger/ledgers/${id}/budget/exchange-rates?${params.toString()}`);
            const rate = res.data?.rates?.[fromCurrency];
            if (rate !== undefined && rate !== null) {
                setFormData(prev => {
                    const currentFromAcc = accounts.find(a => String(a.id) === String(prev.from_account_id));
                    const currentToAcc = accounts.find(a => String(a.id) === String(prev.to_account_id));
                    if (!currentFromAcc || !currentToAcc || currentFromAcc.currency !== fromCurrency || currentToAcc.currency !== toCurrency) {
                        return prev;
                    }
                    const cleanAmount = parseFloat((prev.amount || '0').replace(/,/g, '')) || 0;
                    return {
                        ...prev,
                        exchange_rate: String(rate),
                        to_amount: String((cleanAmount * rate).toFixed(2))
                    };
                });
            }
        } catch (err) {
            console.error("Failed to fetch exchange rate", err);
        }
    }, [api, id, accounts, preferredTimezone]);

    useEffect(() => {
        fetchLedgerDetails();
        fetchCategories();
        fetchCreditCards();
        fetchAccounts();
    }, [fetchLedgerDetails, fetchCategories, fetchCreditCards, fetchAccounts]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    // Handlers
    const formatCurrencyInput = (value) => {
        if (!value) return '';
        let val = value.toString().replace(/[^0-9.]/g, '');
        const parts = val.split('.');
        let cleanVal = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : val;

        if (!cleanVal) return '';

        let [intPart, decPart] = cleanVal.split('.');
        if (intPart === '') intPart = '0';

        if (intPart.length > 1 && intPart.startsWith('0')) {
            intPart = parseInt(intPart, 10).toString();
        }

        const formattedInt = new Intl.NumberFormat('en-US').format(intPart);

        if (decPart !== undefined) {
            decPart = decPart.slice(0, 2);
            return `${formattedInt}.${decPart}`;
        }
        return formattedInt;
    };

    const resetForm = useCallback(() => {
        setCurrentEntry(null);
        setFormData({
            description: '',
            amount: '',
            currency: preferredCurrency,
            date: getTodayInTimezone(preferredTimezone),
            category_id: '',
            type: 'Debit',
            payment_method: 'CASH',
            credit_card_id: '',
            cash_back_amount: '',
            source_document_id: null,
            from_account_id: '',
            to_account_id: '',
            exchange_rate: '',
            to_amount: '',
            feedback: ''
        });

        setReceiptError('');
        setIsParsingReceipt(false);
    }, [preferredCurrency, preferredTimezone]);

    const handleOpenAdd = () => {
        resetForm();
        setOpen(true);
    };

    const handleOpenEdit = (entry) => {
        setCurrentEntry(entry);
        if (entry.isTransfer) {
            setFormData({
                description: entry.description || '',
                amount: entry.from_amount ? String(Number(entry.from_amount)) : '',
                currency: entry.currency || preferredCurrency,
                date: entry.date || '',
                category_id: '',
                type: 'Move',
                payment_method: 'CASH',
                credit_card_id: '',
                cash_back_entry_id: '',
                cash_back_amount: '',
                source_document_id: null,
                from_account_id: entry.from_account_id || '',
                to_account_id: entry.to_account_id || '',
                exchange_rate: entry.exchange_rate ? String(entry.exchange_rate) : '1.0',
                to_amount: entry.to_amount ? String(entry.to_amount) : '',
                feedback: ''
            });
        } else {
            setFormData({
                description: entry.description,
                amount: entry.amount ? Math.abs(Number(entry.amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
                currency: entry.currency || preferredCurrency,
                date: entry.date ? (typeof entry.date === 'string' ? entry.date.split('T')[0] : formatDateInTimezone(entry.date, preferredTimezone)) : '',
                category_id: entry.category_id || '',
                type: entry.amount && Number(entry.amount) < 0 ? 'Credit' : 'Debit',
                payment_method: entry.credit_card_id ? 'CREDIT_CARD' : (entry.cash_back_entry_id ? 'CASH_BACK' : 'CASH'),
                credit_card_id: entry.credit_card_id || '',
                cash_back_entry_id: entry.cash_back_entry_id || '',
                cash_back_amount: entry.linked_cash_back_amount ? String(entry.linked_cash_back_amount) : '',
                source_document_id: entry.source_document_id || null,
                from_account_id: '',
                to_account_id: '',
                exchange_rate: '',
                to_amount: '',
                feedback: ''
            });
        }
        setOpen(true);
        handleCloseMenu();
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            let shouldFetch = true;
            if (formData.type === 'Move') {
                const parsedFrom = parseFloat((formData.amount || '').toString().replace(/,/g, ''));
                const parsedTo = parseFloat((formData.to_amount || formData.amount || '').toString().replace(/,/g, ''));
                const parsedRate = parseFloat((formData.exchange_rate || '1.0').toString().replace(/,/g, ''));
                const payload = {
                    from_account_id: formData.from_account_id ? Number(formData.from_account_id) : null,
                    to_account_id: formData.to_account_id ? Number(formData.to_account_id) : null,
                    from_amount: parsedFrom,
                    to_amount: parsedTo,
                    exchange_rate: parsedRate,
                    date: formData.date ? formData.date : getTodayInTimezone(preferredTimezone),
                    description: formData.description || ''
                };
                if (currentEntry) {
                    await api.put(`/ledger/ledgers/${id}/account-transfers/${currentEntry.transferId}`, payload);
                } else {
                    await api.post(`/ledger/ledgers/${id}/account-transfers/`, payload);
                    setShowTransfers(prev => {
                        if (!prev) {
                            shouldFetch = false;
                            return true;
                        }
                        return prev;
                    });
                }
            } else {
                const parsedAmount = parseFloat((formData.amount || '').toString().replace(/,/g, ''));
                const finalAmount = formData.type === 'Credit' ? -Math.abs(parsedAmount) : Math.abs(parsedAmount);

                const payload = {
                    description: formData.description,
                    amount: finalAmount,
                    currency: formData.currency,
                    date: formData.date ? new Date(formData.date).toISOString() : null,
                    category_id: formData.category_id || null,
                    credit_card_id: formData.payment_method === 'CREDIT_CARD' ? (formData.credit_card_id || null) : null,
                    cash_back_amount: formData.payment_method === 'CASH_BACK' ? parseFloat((formData.cash_back_amount || '').toString().replace(/,/g, '')) : null,
                    cash_back_entry_id: formData.payment_method === 'CASH_BACK' ? (formData.cash_back_entry_id || null) : null,
                    source_document_id: formData.source_document_id || null
                };

                if (currentEntry) {
                    await api.put(`/ledger/${currentEntry.id}`, payload);
                } else {
                    await api.post(`/ledger/?ledger_id=${id}`, payload);
                }
            }
            setOpen(false);
            if (shouldFetch) {
                fetchEntries();
            }
        } catch (err) {
            console.error("Failed to save entry", err);
            showToast("Failed to save transaction.", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedEntryId) return;
        try {
            const entry = entries.find(e => e.id === selectedEntryId);
            if (entry && entry.isTransfer) {
                await api.delete(`/ledger/ledgers/${id}/account-transfers/${entry.transferId}`);
            } else {
                await api.delete(`/ledger/${selectedEntryId}`);
            }
            handleCloseMenu();
            fetchEntries();
        } catch (err) {
            console.error("Failed to delete entry", err);
        }
    };

    // Menu Handlers
    const handleMenuClick = (event, entry) => {
        setAnchorEl(event.currentTarget);
        setSelectedEntryId(entry.id);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedEntryId(null);
    };
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 12 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}>
                        {t('Transactions')}
                    </Typography>
                </Box>

            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* Filters */}
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    mb: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    bgcolor: creamBg,
                    borderRadius: (theme) => `${theme.shape?.containerRadius || 32}px`,
                    border: '1px solid rgba(10, 42, 33, 0.08)',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { xs: 'stretch', md: 'flex-end' } }}>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <FormField label={t('From')} sx={{ flex: 1 }}>
                            <TextField
                                type="date"
                                variant="filled"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ disableUnderline: true }}
                                value={startDate}
                                onChange={(e) => { setStartDate(e.target.value); setActiveFilter('Custom'); setPage(0); }}
                                fullWidth
                                sx={filledInputSx}
                            />
                        </FormField>
                        <FormField label={t('To')} sx={{ flex: 1 }}>
                            <TextField
                                type="date"
                                variant="filled"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ disableUnderline: true }}
                                value={endDate}
                                onChange={(e) => { setEndDate(e.target.value); setActiveFilter('Custom'); setPage(0); }}
                                fullWidth
                                sx={filledInputSx}
                            />
                        </FormField>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <FormField label={t('Search')} sx={{ flex: 1 }}>
                            <TextField
                                variant="filled"
                                placeholder={t('Keywords...')}
                                InputProps={{ disableUnderline: true }}
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                                fullWidth
                                sx={filledInputSx}
                            />
                        </FormField>
                        <FormField label={t('Categories')} sx={{ flex: 1 }}>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                limitTags={1}
                                options={sortCategoriesHierarchically(categories, t)}
                                getOptionLabel={(option) => getCategoryOptionLabel(option, categories, t)}
                                value={selectedCategories}
                                onChange={(event, newValue) => {
                                    setSelectedCategories(newValue);
                                    setPage(0);
                                }}
                                renderOption={(props, option) => {
                                    const { key, ...restProps } = props;
                                    const paddingLeft = option.parent_id ? '24px' : '0px';
                                    return (
                                        <li key={key || option.id} {...restProps}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: paddingLeft }}>
                                                {option.color && (
                                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option.color }} />
                                                )}
                                                {getCategoryOptionLabel(option, categories, t)}
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return (
                                            <Chip
                                                key={key}
                                                variant="outlined"
                                                size="small"
                                                label={getCategoryOptionLabel(option, categories, t)}
                                                {...tagProps}
                                            />
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder={t('Select...')}
                                        sx={{
                                            ...filledInputSx,
                                            '& .MuiFilledInput-root': {
                                                ...filledInputSx['& .MuiFilledInput-root'],
                                                pt: '6px !important', // Allow chips to breathe
                                            }
                                        }}
                                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    />
                                )}
                                fullWidth
                            />
                        </FormField>
                    </Box>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                            setStartDate(initialDates.start);
                            setEndDate(initialDates.end);
                            setActiveFilter('1 Year');
                            setSearch('');
                            setSelectedCategories([]);
                            setPage(0);
                        }}
                        sx={{
                            height: 56,
                            px: 3,
                            ml: { xs: 0, md: 'auto' },
                            fontWeight: 700,
                            color: arborGreen,
                            opacity: 0.7,
                            '&:hover': { opacity: 1, bgcolor: 'rgba(10, 42, 33, 0.05)' }
                        }}
                    >
                        {t('Reset')}
                    </Button>
                </Box>

                {/* Quick Filters */}
                <Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1, alignItems: 'center', width: '100%' }}>
                        {[
                            { label: 'This Month', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); } },
                            { label: '1 Month', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()); } },
                            { label: '3 Months', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()); } },
                            { label: '6 Months', calc: () => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()); } },
                            { label: '1 Year', calc: () => { const now = new Date(); return new Date(now.getFullYear() - 1, now.getMonth(), 1); } },
                            { label: '3 Years', calc: () => { const now = new Date(); return new Date(now.getFullYear() - 3, now.getMonth(), 1); } },
                            { label: 'All Years', calc: null },
                        ].map((filter) => (
                            <Button
                                key={filter.label}
                                variant="filter"
                                color={activeFilter === filter.label ? "primary" : "inherit"}
                                onClick={() => {
                                    if (filter.calc) {
                                        const end = new Date();
                                        const start = filter.calc();
                                        setStartDate(formatDateInTimezone(start, preferredTimezone));
                                        setEndDate(formatDateInTimezone(end, preferredTimezone));
                                    } else {
                                        // All Years
                                        setStartDate('');
                                        setEndDate('');
                                    }
                                    setActiveFilter(filter.label);
                                    setPage(0);
                                }}
                            >
                                {t(filter.label)}
                            </Button>
                        ))}
                        {accounts && accounts.length > 0 && (
                            <Button
                                variant="filter"
                                color={showTransfers ? "primary" : "inherit"}
                                onClick={() => {
                                    setShowTransfers(!showTransfers);
                                    setPage(0);
                                }}
                                sx={{ ml: { xs: 0, sm: 'auto' } }}
                            >
                                {showTransfers ? t('Hide Transfers') : t('Show Transfers')}
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* Scan Document Button */}
            {userRole !== 'viewer' && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        id="scan-document-btn"
                        variant="outlined"
                        startIcon={<DocumentScannerIcon />}
                        onClick={() => setScanDocOpen(true)}
                        sx={{
                            fontWeight: 700,
                            textTransform: 'none',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            px: 3,
                            py: 1,
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06), borderColor: 'primary.main' },
                        }}
                    >
                        {t('Scan Document')}
                    </Button>
                </Box>
            )}

            <LoadingOverlay open={isLoading} />

            {isMobile ? (
                <Stack spacing={2}>
                    {!isLoading && entries?.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
                            <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                            <Typography color="text.secondary">{t('No transactions found')}</Typography>
                        </Paper>
                    ) : (
                        entries?.map((row) => (
                            <TransactionCard
                                key={row.id}
                                row={row}
                                userRole={userRole}
                                handleMenuClick={handleMenuClick}
                                formatCurrency={formatCurrency}
                                onOpenQuickCategory={setSelectedTxForCategory}
                            />
                        ))
                    )}
                </Stack>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: '100%', '& .MuiTableCell-root': { px: { xs: 1, sm: 2 } } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('Date')}</TableCell>
                                <TableCell>{t('Description')}</TableCell>
                                <TableCell>{t('Category')}</TableCell>
                                <TableCell align="right">{t('Amount')}</TableCell>
                                {userRole !== 'viewer' && <TableCell align="right">{t('Actions')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!isLoading && entries?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                        <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                                        <Typography color="text.secondary">{t('No transactions found')}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                entries?.map((row) => (
                                    <TransactionRow
                                        key={row.id}
                                        row={row}
                                        userRole={userRole}
                                        handleMenuClick={handleMenuClick}
                                        formatCurrency={formatCurrency}
                                        onOpenQuickCategory={setSelectedTxForCategory}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
            />

            {/* Floating Add Button */}
            {userRole !== 'viewer' && (
                <Fab color="primary" sx={{ position: 'fixed', bottom: 32, right: 32 }} onClick={handleOpenAdd}>
                    <AddIcon />
                </Fab>
            )}

            {/* Add/Edit Dialog */}
            <FormDialog
                open={open}
                onClose={isSaving ? undefined : () => { setOpen(false); resetForm(); }}
                onSubmit={handleSave}
                title={currentEntry ? t('Edit Transaction') : t('New Transaction')}
                saving={isSaving}
                maxWidth="sm"
            >
                <Stack spacing={4}>
                    {/* Receipt Section */}
                    {formData.type !== 'Move' && (
                        <FormSection title={t("Receipt Scanning")}>
                            <Stack spacing={2}>
                                {receiptError && <Alert severity="error" sx={{ borderRadius: 2 }}>{receiptError}</Alert>}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={<CameraAltIcon />}
                                        onClick={() => {
                                            setCameraOpen(true);
                                            setFormData(prev => ({ ...prev, feedback: '' }));
                                        }}
                                        disabled={isParsingReceipt || isSaving}
                                        sx={{
                                            py: 1.5,
                                            fontWeight: 700,
                                            textTransform: 'none',
                                        }}
                                    >
                                        {t("Scan Receipt")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        fullWidth
                                        startIcon={isParsingReceipt ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                        disabled={isParsingReceipt || isSaving}
                                        sx={{
                                            py: 1.5,
                                            fontWeight: 700,
                                            textTransform: 'none',
                                        }}
                                    >
                                        {isParsingReceipt ? t('Analyzing…') : t('Upload Receipt')}
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                setReceiptError('');
                                                setIsParsingReceipt(true);
                                                setFormData(prev => ({ ...prev, feedback: '' }));

                                                const uploadData = new FormData();
                                                uploadData.append('file', file);
                                                try {
                                                    const res = await api.post(`/source-documents/parse?ledger_id=${id}`, uploadData, {
                                                        headers: { 'Content-Type': 'multipart/form-data' }
                                                    });
                                                    const data = res.data;
                                                    window.dispatchEvent(new Event('quota-updated'));


                                                    let matchedCategoryId = data.category_id || null;
                                                    if (!matchedCategoryId && data.category) {
                                                        const matchedCategory = findMatchingCategory(data.category, categories, 'Debit', t);
                                                        if (matchedCategory) matchedCategoryId = matchedCategory.id;
                                                    }

                                                    let finalPaymentMethod = 'CASH';
                                                    let matchedCreditCardId = '';

                                                    if (data.payment_method === 'CREDIT_CARD' && data.credit_card_id) {
                                                        const ccId = parseInt(data.credit_card_id, 10);
                                                        if (creditCards.some(card => card.id === ccId)) {
                                                            finalPaymentMethod = 'CREDIT_CARD';
                                                            matchedCreditCardId = ccId;
                                                        }
                                                    }

                                                    setFormData(prev => ({
                                                        ...prev,
                                                        description: data.store && data.store !== 'Unknown Store'
                                                            ? data.store
                                                            : (data.items?.length > 0 ? data.items[0].description : prev.description),
                                                        amount: data.total
                                                            ? Math.abs(Number(data.total)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                            : prev.amount,
                                                        currency: data.currency || prev.currency || 'CAD',
                                                        date: data.date || prev.date,
                                                        category_id: matchedCategoryId || prev.category_id,
                                                        type: 'Debit',
                                                        payment_method: finalPaymentMethod,
                                                        credit_card_id: matchedCreditCardId,
                                                        source_document_id: data.source_document_id || null,
                                                        feedback: data.feedback || ''
                                                    }));
                                                    showToast(t('Receipt scanned — please review the details.'), 'success');
                                                } catch (err) {
                                                    console.error('Receipt parse failed:', err);
                                                    setReceiptError(t('Failed to parse receipt. Please enter details manually.'));
                                                } finally {
                                                    setIsParsingReceipt(false);
                                                }
                                            }}
                                        />
                                    </Button>
                                </Box>
                                {formData.feedback && (
                                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                        {formData.feedback}
                                    </Alert>
                                )}
                            </Stack>
                        </FormSection>
                    )}


                    {/* Transaction Details */}
                    <FormSection title={t("Core Information")}>
                        <Stack spacing={3}>
                            <FormField label={t("Description")} id="description" required>
                                <TextField
                                    id="description"
                                    autoFocus
                                    variant="filled"
                                    fullWidth
                                    disabled={isParsingReceipt}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    sx={filledInputSx}
                                    InputProps={{ disableUnderline: true }}
                                />
                            </FormField>

                            <Stack
                                direction={{ xs: 'column-reverse', sm: 'row' }}
                                spacing={3}
                                alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                            >
                                <FormField
                                    label={formData.type === 'Move' ? t("From Amount") : t("Amount")}
                                    id="amount"
                                    required
                                    sx={{ flex: 1, width: '100%' }}
                                >
                                    <TextField
                                        id="amount"
                                        variant="filled"
                                        fullWidth
                                        disabled={isParsingReceipt}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start" sx={{ color: arborGreen, fontWeight: 700, height: '100%', mt: 0 }}>$</InputAdornment>,
                                            disableUnderline: true
                                        }}
                                        slotProps={{
                                            htmlInput: { inputMode: 'decimal' }
                                        }}
                                        value={formData.amount}
                                        onFocus={(e) => handleNumericFocus(e)}
                                        onChange={(e) => {
                                            const newAmount = formatCurrencyInput(e.target.value);
                                            setFormData(prev => {
                                                const updated = {
                                                    ...prev,
                                                    amount: newAmount,
                                                    cash_back_amount: (prev.payment_method === 'CASH_BACK' && isSyncingCashback) ? newAmount : prev.cash_back_amount
                                                };
                                                if (prev.type === 'Move') {
                                                    const fromAcc = accounts.find(a => String(a.id) === String(prev.from_account_id));
                                                    const toAcc = accounts.find(a => String(a.id) === String(prev.to_account_id));
                                                    const cleanAmount = parseFloat(newAmount.replace(/,/g, '')) || 0;
                                                    if (fromAcc && toAcc && fromAcc.currency === toAcc.currency) {
                                                        updated.to_amount = String(cleanAmount);
                                                        updated.exchange_rate = '1.0';
                                                    } else {
                                                        const rate = parseFloat(prev.exchange_rate || '1.0') || 0;
                                                        updated.to_amount = String((cleanAmount * rate).toFixed(2));
                                                    }
                                                }
                                                return updated;
                                            });
                                        }}
                                        onBlur={() => setIsSyncingCashback(false)}
                                        sx={filledInputSx}
                                    />
                                </FormField>

                                <FormField
                                    label={t("Type")}
                                    id="transaction_type"
                                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                                >
                                    <ToggleButtonGroup
                                        id="transaction_type"
                                        value={formData.type}
                                        exclusive
                                        disabled={isParsingReceipt}
                                        onChange={(e, newType) => {
                                            if (newType !== null) {
                                                let targetFromId = formData.from_account_id;
                                                let targetToId = formData.to_account_id;
                                                if (newType === 'Move' && accounts && accounts.length > 0) {
                                                    targetFromId = formData.from_account_id || accounts[0].id;
                                                    targetToId = formData.to_account_id || (accounts.find(a => a.id !== targetFromId) || accounts[0]).id;
                                                }
                                                setFormData(prev => {
                                                    const updated = {
                                                        ...prev,
                                                        type: newType,
                                                    };
                                                    if (newType === 'Credit') {
                                                        updated.payment_method = 'CASH';
                                                        updated.credit_card_id = '';
                                                        updated.cash_back_amount = '';
                                                        updated.cash_back_entry_id = null;
                                                    } else if (newType === 'Move') {
                                                        if (accounts && accounts.length > 0) {
                                                            updated.from_account_id = targetFromId;
                                                            updated.to_account_id = targetToId;
                                                            const fromAcc = accounts.find(a => a.id === targetFromId);
                                                            const toAcc = accounts.find(a => a.id === targetToId);
                                                            if (fromAcc) {
                                                                updated.currency = fromAcc.currency;
                                                            }
                                                            if (fromAcc && toAcc && fromAcc.currency === toAcc.currency) {
                                                                updated.exchange_rate = '1.0';
                                                                updated.to_amount = (prev.amount || '').replace(/,/g, '');
                                                            } else {
                                                                updated.exchange_rate = prev.exchange_rate || '1.0';
                                                                const rate = parseFloat(prev.exchange_rate || '1.0');
                                                                const amount = parseFloat((prev.amount || '0').replace(/,/g, ''));
                                                                updated.to_amount = String((amount * rate).toFixed(2));
                                                            }
                                                        }
                                                    }
                                                    return updated;
                                                });

                                                if (newType === 'Move' && accounts && accounts.length > 0) {
                                                    const fromAcc = accounts.find(a => a.id === targetFromId);
                                                    const toAcc = accounts.find(a => a.id === targetToId);
                                                    if (fromAcc && toAcc && fromAcc.currency !== toAcc.currency) {
                                                        fetchAndSetExchangeRate(fromAcc.currency, toAcc.currency, formData.date || getTodayInTimezone(preferredTimezone));
                                                    }
                                                }
                                            }
                                        }}
                                        sx={{
                                            width: { xs: '100%', sm: 'auto' },
                                            display: 'flex',
                                            '& .MuiToggleButton-root': {
                                                flex: { xs: 1, sm: 'initial' },
                                                px: { xs: 1.5, sm: 2 }
                                            }
                                        }}
                                    >
                                        <ToggleButton value="Debit" color="error">{t("Expense")}</ToggleButton>
                                        <ToggleButton value="Credit" color="success">{t("Income")}</ToggleButton>
                                        {accounts && accounts.length > 0 && (
                                            <ToggleButton value="Move" color="primary">{t("Transfer")}</ToggleButton>
                                        )}
                                    </ToggleButtonGroup>
                                </FormField>
                            </Stack>
                        </Stack>
                    </FormSection>

                    {/* Transfer Details FormSection */}
                    {formData.type === 'Move' && (
                        <FormSection title={t("Transfer Details")}>
                            <Stack spacing={3}>
                                <FormField label={t("Date")} id="date" required>
                                    <TextField
                                        id="date"
                                        type="date"
                                        variant="filled"
                                        fullWidth
                                        value={formData.date}
                                        onChange={(e) => {
                                            const newDate = e.target.value;
                                            setFormData(prev => ({ ...prev, date: newDate }));
                                            const fromAcc = accounts.find(a => String(a.id) === String(formData.from_account_id));
                                            const toAcc = accounts.find(a => String(a.id) === String(formData.to_account_id));
                                            if (fromAcc && toAcc && fromAcc.currency !== toAcc.currency) {
                                                fetchAndSetExchangeRate(fromAcc.currency, toAcc.currency, newDate);
                                            }
                                        }}
                                        sx={filledInputSx}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ disableUnderline: true }}
                                    />
                                </FormField>

                                <Stack direction="row" spacing={3}>
                                    <FormField label={t("From Account")} id="from_account_id" required sx={{ flex: 1 }}>
                                        <TextField
                                            id="from_account_id"
                                            select
                                            variant="filled"
                                            fullWidth
                                            value={formData.from_account_id}
                                            onChange={(e) => {
                                                const fromId = e.target.value;
                                                setFormData(prev => {
                                                    const updated = {
                                                        ...prev,
                                                        from_account_id: fromId
                                                    };
                                                    if (fromId && String(fromId) === String(prev.to_account_id)) {
                                                        updated.to_account_id = '';
                                                    }
                                                    const fromAcc = accounts.find(a => String(a.id) === String(fromId));
                                                    const toAcc = accounts.find(a => String(a.id) === String(prev.to_account_id));
                                                    if (fromAcc) {
                                                        updated.currency = fromAcc.currency;
                                                    }
                                                    if (fromAcc && toAcc) {
                                                        if (fromAcc.currency === toAcc.currency) {
                                                            updated.exchange_rate = '1.0';
                                                            updated.to_amount = (prev.amount || '').replace(/,/g, '');
                                                        } else {
                                                            const rate = parseFloat(prev.exchange_rate || '1.0');
                                                            const amount = parseFloat((prev.amount || '0').replace(/,/g, ''));
                                                            updated.to_amount = String((amount * rate).toFixed(2));
                                                        }
                                                    }
                                                    return updated;
                                                });
                                                const fromAcc = accounts.find(a => String(a.id) === String(fromId));
                                                const toAcc = accounts.find(a => String(a.id) === String(formData.to_account_id));
                                                if (fromAcc && toAcc && fromAcc.currency !== toAcc.currency) {
                                                    fetchAndSetExchangeRate(fromAcc.currency, toAcc.currency, formData.date || getTodayInTimezone(preferredTimezone));
                                                }
                                            }}
                                            sx={filledInputSx}
                                            SelectProps={{ disableUnderline: true }}
                                        >
                                            {accounts.map(acc => (
                                                <MenuItem key={acc.id} value={acc.id}>
                                                    {t(acc.account_type)} ({acc.currency})
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </FormField>

                                    <FormField label={t("To Account")} id="to_account_id" required sx={{ flex: 1 }}>
                                        <TextField
                                            id="to_account_id"
                                            select
                                            variant="filled"
                                            fullWidth
                                            value={formData.to_account_id}
                                            onChange={(e) => {
                                                const toId = e.target.value;
                                                setFormData(prev => {
                                                    const updated = {
                                                        ...prev,
                                                        to_account_id: toId
                                                    };
                                                    const fromAcc = accounts.find(a => String(a.id) === String(prev.from_account_id));
                                                    const toAcc = accounts.find(a => String(a.id) === String(toId));
                                                    if (fromAcc && toAcc) {
                                                        if (fromAcc.currency === toAcc.currency) {
                                                            updated.exchange_rate = '1.0';
                                                            updated.to_amount = (prev.amount || '').replace(/,/g, '');
                                                        } else {
                                                            const rate = parseFloat(prev.exchange_rate || '1.0');
                                                            const amount = parseFloat((prev.amount || '0').replace(/,/g, ''));
                                                            updated.to_amount = String((amount * rate).toFixed(2));
                                                        }
                                                    }
                                                    return updated;
                                                });
                                                const fromAcc = accounts.find(a => String(a.id) === String(formData.from_account_id));
                                                const toAcc = accounts.find(a => String(a.id) === String(toId));
                                                if (fromAcc && toAcc && fromAcc.currency !== toAcc.currency) {
                                                    fetchAndSetExchangeRate(fromAcc.currency, toAcc.currency, formData.date || getTodayInTimezone(preferredTimezone));
                                                }
                                            }}
                                            sx={filledInputSx}
                                            SelectProps={{ disableUnderline: true }}
                                        >
                                            {accounts
                                                .filter(acc => String(acc.id) !== String(formData.from_account_id))
                                                .map(acc => (
                                                    <MenuItem key={acc.id} value={acc.id}>
                                                        {t(acc.account_type)} ({acc.currency})
                                                    </MenuItem>
                                                ))}
                                        </TextField>
                                    </FormField>
                                </Stack>

                                {(() => {
                                    const fromAcc = accounts.find(a => String(a.id) === String(formData.from_account_id));
                                    const toAcc = accounts.find(a => String(a.id) === String(formData.to_account_id));
                                    const isCrossCurrency = fromAcc && toAcc && fromAcc.currency !== toAcc.currency;

                                    if (!isCrossCurrency) return null;

                                    const cleanFrom = parseFloat((formData.amount || '0').replace(/,/g, ''));
                                    const cleanRate = parseFloat(formData.exchange_rate || '1.0');
                                    const cleanTo = parseFloat((formData.to_amount || '0').replace(/,/g, ''));

                                    return (
                                        <Stack spacing={3}>
                                            <Stack direction="row" spacing={3}>
                                                <FormField label={t("Exchange Rate")} id="exchange_rate" required sx={{ flex: 1 }}>
                                                    <TextField
                                                        id="exchange_rate"
                                                        variant="filled"
                                                        fullWidth
                                                        slotProps={{
                                                            htmlInput: { inputMode: 'decimal' }
                                                        }}
                                                        value={formData.exchange_rate}
                                                        onFocus={(e) => handleNumericFocus(e)}
                                                        onChange={(e) => {
                                                            const rateVal = e.target.value;
                                                            setFormData(prev => {
                                                                const updated = {
                                                                    ...prev,
                                                                    exchange_rate: rateVal
                                                                };
                                                                const rate = parseFloat(rateVal || '0');
                                                                const amount = parseFloat((prev.amount || '0').replace(/,/g, ''));
                                                                updated.to_amount = String((amount * rate).toFixed(2));
                                                                return updated;
                                                            });
                                                        }}
                                                        sx={filledInputSx}
                                                        InputProps={{ disableUnderline: true }}
                                                    />
                                                </FormField>

                                                <FormField label={t("To Amount")} id="to_amount" required sx={{ flex: 1 }}>
                                                    <TextField
                                                        id="to_amount"
                                                        variant="filled"
                                                        fullWidth
                                                        slotProps={{
                                                            htmlInput: { inputMode: 'decimal' }
                                                        }}
                                                        value={formData.to_amount}
                                                        onFocus={(e) => handleNumericFocus(e)}
                                                        onChange={(e) => {
                                                            const toVal = e.target.value;
                                                            setFormData(prev => {
                                                                const updated = {
                                                                    ...prev,
                                                                    to_amount: toVal
                                                                };
                                                                const toAmt = parseFloat(toVal || '0');
                                                                const fromAmt = parseFloat((prev.amount || '0').replace(/,/g, ''));
                                                                if (fromAmt > 0) {
                                                                    updated.exchange_rate = String(toAmt / fromAmt);
                                                                }
                                                                return updated;
                                                            });
                                                        }}
                                                        sx={filledInputSx}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start" sx={{ color: arborGreen, fontWeight: 700, mt: 0 }}>$</InputAdornment>,
                                                            disableUnderline: true
                                                        }}
                                                    />
                                                </FormField>
                                            </Stack>
                                            <Typography variant="caption" sx={{ color: 'rgba(10, 42, 33, 0.6)', fontStyle: 'italic', textAlign: 'center' }}>
                                                {cleanFrom.toFixed(2)} {fromAcc.currency} &times; {cleanRate.toFixed(4)} = {cleanTo.toFixed(2)} {toAcc.currency}
                                            </Typography>
                                        </Stack>
                                    );
                                })()}
                            </Stack>
                        </FormSection>
                    )}

                    {/* Metadata Section */}
                    {formData.type !== 'Move' && (
                        <FormSection title={t("Organization & Time")}>
                            <Stack spacing={3}>
                                <Stack direction="row" spacing={3}>
                                    <FormField label={t("Date")} id="date" required sx={{ flex: 1 }}>
                                        <TextField
                                            id="date"
                                            type="date"
                                            variant="filled"
                                            fullWidth
                                            disabled={isParsingReceipt}
                                            value={formData.date}
                                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                            sx={filledInputSx}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{ disableUnderline: true }}
                                        />
                                    </FormField>
                                    <FormField label={t("Currency")} id="currency" required sx={{ flex: 1 }}>
                                        <Autocomplete
                                            id="currency"
                                            options={[Currency.CAD, Currency.USD, Currency.CNY]}
                                            value={formData.currency || Currency.CAD}
                                            onChange={(e, newValue) => {
                                                if (newValue) {
                                                    setFormData(prev => ({ ...prev, currency: newValue }));
                                                }
                                            }}
                                            disableClearable
                                            autoHighlight
                                            disabled={isParsingReceipt}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="filled"
                                                    fullWidth
                                                    sx={filledInputSx}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                                />
                                            )}
                                        />
                                    </FormField>
                                </Stack>

                                <FormField label={t("Category")} id="category_id">
                                    <Autocomplete
                                        id="category_id"
                                        openOnFocus
                                        options={filterAndSortCategories(categories, null, t)}
                                        getOptionLabel={(option) => getCategoryOptionLabel(option, categories, t)}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        value={categories.find(c => c.id === formData.category_id) || null}
                                        onChange={(event, newValue) => {
                                            setFormData({ ...formData, category_id: newValue ? newValue.id : '' });
                                        }}
                                        disabled={isParsingReceipt}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="filled"
                                                fullWidth
                                                sx={filledInputSx}
                                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const { key, ...restProps } = props;
                                            const paddingLeft = option.parent_id ? '24px' : '0px';
                                            return (
                                                <li key={key || option.id} {...restProps}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: paddingLeft }}>
                                                        {option.color && (
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option.color }} />
                                                        )}
                                                        {t(option.name)}
                                                    </Box>
                                                </li>
                                            );
                                        }}
                                    />
                                </FormField>
                            </Stack>
                        </FormSection>
                    )}

                    {/* Payment Method Section (Only for Debits) */}
                    {formData.type === 'Debit' && (
                        <FormSection title={t("Payment Method")}>
                            <Stack spacing={3}>
                                <FormField label={t("Method")}>
                                    <ToggleButtonGroup
                                        value={formData.payment_method}
                                        exclusive
                                        fullWidth
                                        onChange={(e, v) => v && setFormData({ ...formData, payment_method: v })}
                                        sx={{}}
                                    >
                                        <ToggleButton value="CASH">{t("Cash")}</ToggleButton>
                                        <ToggleButton value="CREDIT_CARD">{t("Credit Card")}</ToggleButton>
                                        <ToggleButton value="CASH_BACK">{t("Cash Back")}</ToggleButton>
                                    </ToggleButtonGroup>
                                </FormField>

                                {formData.payment_method === 'CREDIT_CARD' && (
                                    <FormField label={t("Card")} required>
                                        <TextField
                                            select
                                            variant="filled"
                                            fullWidth
                                            value={formData.credit_card_id}
                                            onChange={(e) => setFormData({ ...formData, credit_card_id: e.target.value })}
                                            sx={filledInputSx}
                                            SelectProps={{ disableUnderline: true }}
                                        >
                                            {creditCards.map(card => (
                                                <MenuItem key={card.id} value={card.id}>
                                                    {card.name} (..{card.last_4_digits})
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </FormField>
                                )}

                                {formData.payment_method === 'CASH_BACK' && (
                                    <Stack spacing={3}>
                                        <FormField label={t("Link to Expense")}>
                                            <TextField
                                                select
                                                variant="filled"
                                                fullWidth
                                                value={formData.cash_back_entry_id || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const entry = entries.find(en => en.id === val);
                                                    setFormData({
                                                        ...formData,
                                                        cash_back_entry_id: val,
                                                        cash_back_amount: entry ? Math.abs(Number(entry.amount)).toString() : formData.cash_back_amount
                                                    });
                                                    if (entry) setIsSyncingCashback(true);
                                                }}
                                                sx={filledInputSx}
                                                SelectProps={{ disableUnderline: true }}
                                            >
                                                {entries.filter(e => Number(e.amount) > 0).slice(0, 10).map(e => (
                                                    <MenuItem key={e.id} value={e.id}>
                                                        {new Date(e.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })} - {e.description} ({formatCurrency(e.amount, e.currency)})
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </FormField>
                                        <FormField label={t("Cash Back Amount")} required>
                                            <TextField
                                                variant="filled"
                                                fullWidth
                                                slotProps={{
                                                    htmlInput: { inputMode: 'decimal' }
                                                }}
                                                value={formData.cash_back_amount}
                                                onFocus={(e) => handleNumericFocus(e)}
                                                onChange={(e) => setFormData({ ...formData, cash_back_amount: formatCurrencyInput(e.target.value) })}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start" sx={{ color: arborGreen, fontWeight: 700 }}>$</InputAdornment>,
                                                    disableUnderline: true
                                                }}
                                                sx={filledInputSx}
                                            />
                                        </FormField>
                                    </Stack>
                                )}
                            </Stack>
                        </FormSection>
                    )}
                </Stack>
            </FormDialog>

            {/* Action Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                {userRole !== 'viewer' && (
                    <>
                        <Tooltip title={entries.find(e => e.id === selectedEntryId)?.is_cash_back_reward ? t("Managed by parent transaction") : ""}>
                            <span>
                                <MenuItem
                                    onClick={() => handleOpenEdit(entries.find(e => e.id === selectedEntryId))}
                                    disabled={entries.find(e => e.id === selectedEntryId)?.is_cash_back_reward}
                                >
                                    {t("Edit")}
                                </MenuItem>
                            </span>
                        </Tooltip>
                        <MenuItem
                            onClick={handleDelete}
                            sx={{ color: 'error.main' }}
                            disabled={entries.find(e => e.id === selectedEntryId)?.is_cash_back_reward}
                        >
                            {t("Delete")}
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Scan Document Dialog */}
            <ScanDocumentDialog
                open={scanDocOpen}
                onClose={() => setScanDocOpen(false)}
                api={api}
                ledgerId={id}
                categories={categories}
                creditCards={creditCards}
                accounts={accounts}
                onSaved={() => {
                    setScanDocOpen(false);
                    fetchEntries();
                    showToast(t('Transactions saved successfully.'), 'success');
                }}
            />

            {/* Receipt Camera Dialog */}
            <ReceiptCameraDialog
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                api={api}
                ledgerId={id}
                onParsed={(data) => {
                    setCameraOpen(false);

                    let matchedCategoryId = data.category_id || null;
                    if (!matchedCategoryId && data.category) {
                        const matchedCategory = findMatchingCategory(data.category, categories, 'Debit', t);
                        if (matchedCategory) matchedCategoryId = matchedCategory.id;
                    }

                    // Receipt mapping for payment method
                    let finalPaymentMethod = 'CASH';
                    let matchedCreditCardId = '';

                    if (data.payment_method === 'CREDIT_CARD' && data.credit_card_id) {
                        const ccId = parseInt(data.credit_card_id, 10);
                        if (creditCards.some(card => card.id === ccId)) {
                            finalPaymentMethod = 'CREDIT_CARD';
                            matchedCreditCardId = ccId;
                        }
                    }

                    setFormData(prev => ({
                        ...prev,
                        description: data.store && data.store !== 'Unknown Store'
                            ? data.store
                            : (data.items?.length > 0 ? data.items[0].description : prev.description),
                        amount: data.total
                            ? Math.abs(Number(data.total)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : prev.amount,
                        currency: data.currency || prev.currency || 'CAD',
                        date: data.date || prev.date,
                        category_id: matchedCategoryId || prev.category_id,
                        type: 'Debit',
                        payment_method: finalPaymentMethod,
                        credit_card_id: matchedCreditCardId,
                        cash_back_amount: '', // Clear cash back if any
                        source_document_id: data.source_document_id || null,
                        feedback: data.feedback || ''
                    }));

                    showToast(t('Receipt scanned — please review the details.'), 'success');
                }}
            />

            {/* ── Quick Category Select Dialog ── */}
            <QuickCategorySelectDialog
                open={Boolean(selectedTxForCategory)}
                onClose={() => setSelectedTxForCategory(null)}
                currentCategoryId={selectedTxForCategory?.category_id || selectedTxForCategory?.category?.id || null}
                categories={categories}
                onSelectCategory={handleSelectQuickCategory}
                isSaving={isQuickCategorySaving}
            />
        </Container>
    );
}
